/**
 * FlameIcon — 5-layer embossed die-print SVG
 *
 * Brand identity flame for the Firebreak v3 design system.
 * Spec: docs/superpowers/specs/2026-03-17-layout-information-density-design.md §4.1
 *
 * 5-layer construction (bottom to top):
 *   1. Dark shadow path offset +0.8px down: fill rgba(0,0,0,0.3)
 *   2. White highlight path offset -0.6px up: fill rgba(255,255,255,0.5)
 *   3. Gradient fill (main flame): bottom-to-top #f97316 → #fb923c → #fbbf24
 *   4. Highlight stroke offset -0.3px up: stroke rgba(255,255,255,0.35) 0.5px
 *   5. Shadow stroke offset +0.4px down: stroke rgba(0,0,0,0.2) 0.5px
 *
 * Filter: crisp amber halo (NOT diffuse grey)
 *   Light: drop-shadow(0 0 1px rgba(251,146,36,0.6)) drop-shadow(0 0 2px rgba(251,191,36,0.25))
 *   Dark:  drop-shadow(0 0 1.5px rgba(251,146,36,0.7)) drop-shadow(0 0 3px rgba(251,191,36,0.2))
 *
 * Sizes: 20px (nav header), 18px (mobile/collapsed), 32px (login/loading)
 */

interface FlameIconProps {
  size?: number;
  className?: string;
  /** Unique ID for gradient defs — required when multiple instances render on same page */
  id?: string;
  /** Use dark halo filter variant */
  dark?: boolean;
}

// Lucide-style flame path (24x24 viewBox)
const FLAME_PATH =
  'M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5z';

export function FlameIcon({ size = 20, className = '', id = 'flame-grad', dark = false }: FlameIconProps) {
  const px = `${size}px`;

  // Amber halo filter — spec §4.1
  const haloFilter = dark
    ? 'drop-shadow(0 0 1.5px rgba(251,146,36,0.7)) drop-shadow(0 0 3px rgba(251,191,36,0.2))'
    : 'drop-shadow(0 0 1px rgba(251,146,36,0.6)) drop-shadow(0 0 2px rgba(251,191,36,0.25))';

  return (
    <svg
      viewBox="0 0 24 24"
      width={px}
      height={px}
      className={`shrink-0 ${className}`}
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      style={{ filter: haloFilter }}
    >
      <defs>
        {/* Gradient: bottom-to-top per spec — y1=22 (bottom) to y2=2 (top) */}
        <linearGradient id={id} x1="12" y1="22" x2="12" y2="2" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="#f97316" />   {/* orange-600 at bottom */}
          <stop offset="50%" stopColor="#fb923c" />   {/* orange-400 mid */}
          <stop offset="100%" stopColor="#fbbf24" />  {/* amber-400 at top */}
        </linearGradient>
      </defs>

      {/* Layer 1: Dark shadow — offset +0.8px down */}
      <path
        d={FLAME_PATH}
        fill="rgba(0,0,0,0.3)"
        transform="translate(0, 0.8)"
      />

      {/* Layer 2: White highlight — offset -0.6px up */}
      <path
        d={FLAME_PATH}
        fill="rgba(255,255,255,0.5)"
        transform="translate(0, -0.6)"
      />

      {/* Layer 3: Main gradient fill */}
      <path
        d={FLAME_PATH}
        fill={`url(#${id})`}
      />

      {/* Layer 4: Highlight stroke — offset -0.3px up */}
      <path
        d={FLAME_PATH}
        fill="none"
        stroke="rgba(255,255,255,0.35)"
        strokeWidth="0.5"
        transform="translate(0, -0.3)"
      />

      {/* Layer 5: Shadow stroke — offset +0.4px down */}
      <path
        d={FLAME_PATH}
        fill="none"
        stroke="rgba(0,0,0,0.2)"
        strokeWidth="0.5"
        transform="translate(0, 0.4)"
      />
    </svg>
  );
}
